import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Warning, CheckCircle, Trash, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export function IncongruenzeSection({ anno }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getIncongruenze(anno);
      setData(res.data);
    } catch { toast.error('Errore caricamento incongruenze'); }
    finally { setLoading(false); }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Eliminare questa prenotazione orfana?')) return;
    try {
      await api.deleteBooking(bookingId);
      toast.success('Prenotazione eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleDeleteConfirmation = async (confId) => {
    if (!window.confirm('Eliminare questa conferma orfana?')) return;
    try {
      await api.deleteLessonConfirmation(confId);
      toast.success('Conferma eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const counts = data?.counts || { orphan_bookings: 0, orphan_confirmations: 0, orphan_lessons_no_student: 0, duplicated_slots: 0 };
  const totalIssues = counts.orphan_bookings + counts.orphan_confirmations + counts.orphan_lessons_no_student + counts.duplicated_slots;

  return (
    <>
      <div className="mb-6 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Incongruenze</h1>
          <p className="text-[#52525B] text-sm">Verifica coerenza tra tabellone orario, prenotazioni e conferme iscrizioni {anno ? `(anno ${anno})` : ''}</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="incongruenze-refresh">
          <ArrowsClockwise size={16} className="mr-2" />Ricarica
        </Button>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 animate-fade-in animate-delay-1">
        <Card label="Prenotazioni orfane" count={counts.orphan_bookings} color="cyan" />
        <Card label="Conferme orfane" count={counts.orphan_confirmations} color="purple" />
        <Card label="Lezioni senza alunno" count={counts.orphan_lessons_no_student} color="red" />
        <Card label="Slot duplicati" count={counts.duplicated_slots} color="amber" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : totalIssues === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm animate-fade-in animate-delay-2">
          <CheckCircle size={48} className="mx-auto text-green-500 mb-4" weight="fill" />
          <p className="text-[#52525B] font-medium">Nessuna incongruenza rilevata</p>
          <p className="text-[#A1A1AA] text-sm mt-1">Tabellone, prenotazioni e conferme sono coerenti</p>
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in animate-delay-2">
          {/* Prenotazioni orfane */}
          {counts.orphan_bookings > 0 && (
            <Section title="Prenotazioni accettate senza lezione collegata" count={counts.orphan_bookings} color="cyan">
              <table className="data-table" data-testid="orphan-bookings-table">
                <thead><tr><th>Alunno</th><th>Insegnante</th><th>Giorno/Ora</th><th>Durata</th><th>Stato</th><th>Anno</th><th>Motivo</th><th>Azione</th></tr></thead>
                <tbody>{data.orphan_bookings.map(b => (
                  <tr key={b.id} className="border-b border-[#E4E4E7]" data-testid={`orphan-booking-${b.id}`}>
                    <td className="uppercase font-medium">{b.nome}</td>
                    <td className="uppercase text-[#52525B] text-xs">{b.teacher_name || '-'}</td>
                    <td className="text-xs">{DAY_NAMES[b.day] || '?'} {b.start_time}-{b.end_time}</td>
                    <td className="text-xs text-[#52525B]">{b.durata}m</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-cyan-50 text-cyan-700">{b.stato}</span></td>
                    <td className="text-xs font-['Work_Sans']">{b.anno_accademico}</td>
                    <td className="text-xs text-red-600">{b.motivo}</td>
                    <td><button onClick={() => handleDeleteBooking(b.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Elimina prenotazione orfana" data-testid={`del-orphan-booking-${b.id}`}><Trash size={14} /></button></td>
                  </tr>
                ))}</tbody>
              </table>
            </Section>
          )}

          {/* Conferme orfane */}
          {counts.orphan_confirmations > 0 && (
            <Section title="Conferme iscrizione senza lezione collegata" count={counts.orphan_confirmations} color="purple">
              <table className="data-table" data-testid="orphan-confirmations-table">
                <thead><tr><th>Alunno</th><th>Anno target</th><th>Stato pagamento</th><th>Lezioni mancanti</th><th>Motivo</th><th>Azione</th></tr></thead>
                <tbody>{data.orphan_confirmations.map(c => (
                  <tr key={c.id} className="border-b border-[#E4E4E7]" data-testid={`orphan-conf-${c.id}`}>
                    <td className="uppercase font-medium">{c.student_name}</td>
                    <td className="text-xs font-['Work_Sans']">{c.target_year ? `${c.target_year - 1}/${String(c.target_year).slice(-2)}` : '-'}</td>
                    <td className="text-xs">{c.payment_status || '-'}</td>
                    <td className="text-[10px] font-mono text-[#71717A]">{c.lesson_ids_missing?.length ? c.lesson_ids_missing.join(', ') : '-'}</td>
                    <td className="text-xs text-red-600">{c.motivo}</td>
                    <td><button onClick={() => handleDeleteConfirmation(c.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Elimina conferma orfana" data-testid={`del-orphan-conf-${c.id}`}><Trash size={14} /></button></td>
                  </tr>
                ))}</tbody>
              </table>
            </Section>
          )}

          {/* Lezioni con alunno inesistente */}
          {counts.orphan_lessons_no_student > 0 && (
            <Section title="Lezioni con alunno inesistente" count={counts.orphan_lessons_no_student} color="red">
              <table className="data-table" data-testid="orphan-lessons-table">
                <thead><tr><th>Insegnante</th><th>Giorno/Ora</th><th>Anno</th><th>Student ID (invalido)</th><th>Motivo</th></tr></thead>
                <tbody>{data.orphan_lessons_no_student.map(l => (
                  <tr key={l.id} className="border-b border-[#E4E4E7]" data-testid={`orphan-lesson-${l.id}`}>
                    <td className="uppercase text-xs">{l.teacher_name}</td>
                    <td className="text-xs">{DAY_NAMES[l.day] || '?'} {l.start_time}-{l.end_time}</td>
                    <td className="text-xs font-['Work_Sans']">{l.anno}</td>
                    <td className="text-[10px] font-mono text-[#71717A]">{l.student_id}</td>
                    <td className="text-xs text-red-600">{l.motivo}</td>
                  </tr>
                ))}</tbody>
              </table>
              <p className="text-[10px] text-[#71717A] italic mt-2">Suggerimento: elimina queste lezioni dal tabellone se l'alunno non esiste più.</p>
            </Section>
          )}

          {/* Slot duplicati */}
          {counts.duplicated_slots > 0 && (
            <Section title="Slot duplicati (più lezioni sullo stesso orario)" count={counts.duplicated_slots} color="amber">
              <table className="data-table" data-testid="duplicated-slots-table">
                <thead><tr><th>Insegnante</th><th>Giorno/Ora</th><th>Anno</th><th>Lezioni</th></tr></thead>
                <tbody>{data.duplicated_slots.map((d, i) => (
                  <tr key={i} className="border-b border-[#E4E4E7]" data-testid={`duplicated-slot-${i}`}>
                    <td className="uppercase text-xs">{d.teacher_name}</td>
                    <td className="text-xs">{DAY_NAMES[d.day] || '?'} {d.start_time}</td>
                    <td className="text-xs font-['Work_Sans']">{d.anno}</td>
                    <td className="text-xs">
                      <ul className="space-y-0.5">
                        {d.lessons.map(l => (
                          <li key={l.id} className="uppercase"><span className="font-medium">{l.student_name}</span> <span className="text-[#71717A] font-['Work_Sans']">({l.end_time})</span></li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
              <p className="text-[10px] text-[#71717A] italic mt-2">Suggerimento: elimina i duplicati dal tabellone (il cascade rimuoverà anche prenotazioni/conferme collegate).</p>
            </Section>
          )}
        </div>
      )}
    </>
  );
}

const cardColors = {
  cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
  purple: 'bg-purple-50 border-purple-200 text-purple-800',
  red: 'bg-red-50 border-red-200 text-red-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
};

function Card({ label, count, color }) {
  const cls = cardColors[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  return (
    <div className={`border rounded-sm p-3 ${cls}`} data-testid={`incongruenze-card-${color}`}>
      <div className="flex items-center gap-2 mb-1">
        <Warning size={16} weight="fill" />
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="text-2xl font-bold font-['Work_Sans']">{count}</div>
    </div>
  );
}

function Section({ title, count, color, children }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
      <h2 className="font-semibold text-sm mb-3 flex items-center gap-2">
        <Warning size={16} className={`text-${color}-600`} weight="fill" />
        {title} <span className={`px-2 py-0.5 rounded text-[10px] font-bold bg-${color}-100 text-${color}-800`}>{count}</span>
      </h2>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
